import React from 'react'
import { useTranslation } from 'react-i18next'

export type ModalType = 'confirm' | 'error' | 'info' | 'success'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm?: () => void
  title: string
  message: string
  type?: ModalType
  confirmText?: string
  cancelText?: string
  isLoading?: boolean
}

const typeStyles = {
  confirm: {
    icon: (
      <svg className="w-6 h-6 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    ),
    bg: 'bg-yellow-500/20',
    confirmBtn: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20 hover:bg-yellow-500/20',
  },
  error: {
    icon: (
      <svg className="w-6 h-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    bg: 'bg-red-500/20',
    confirmBtn: 'bg-red-500/10 text-red-400 border-red-500/20 hover:bg-red-500/20',
  },
  success: {
    icon: (
      <svg className="w-6 h-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    bg: 'bg-green-500/20',
    confirmBtn: 'bg-green-500/10 text-green-400 border-green-500/20 hover:bg-green-500/20',
  },
  info: {
    icon: (
      <svg className="w-6 h-6 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    bg: 'bg-blue-500/20',
    confirmBtn: 'bg-blue-500/10 text-blue-400 border-blue-500/20 hover:bg-blue-500/20',
  },
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  type = 'info',
  confirmText,
  cancelText,
  isLoading = false,
}) => {
  const { t } = useTranslation()
  
  if (!isOpen) return null
  
  const styles = typeStyles[type]
  const showCancel = type === 'confirm' || onConfirm
  const btnText = confirmText || (type === 'confirm' ? t('common.confirm') : t('common.ok'))
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-[#1a1a25] border border-white/10 rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-10 h-10 rounded-full ${styles.bg} flex items-center justify-center flex-shrink-0`}>
            {styles.icon}
          </div>
          <h3 className="text-lg font-semibold text-white">{title}</h3>
        </div>
        <p className="text-gray-400 mb-6 whitespace-pre-wrap">{message}</p>
        <div className="flex gap-3 justify-end">
          {showCancel && (
            <button
              onClick={onClose}
              className="px-4 py-2 bg-white/5 text-gray-300 border border-white/10 rounded-lg hover:bg-white/10 transition-colors cursor-pointer"
            >
              {cancelText || t('common.cancel')}
            </button>
          )}
          <button
            onClick={onConfirm || onClose}
            disabled={isLoading}
            className={`px-4 py-2 border rounded-lg transition-colors disabled:opacity-50 cursor-pointer ${styles.confirmBtn}`}
          >
            {isLoading ? t('common.loading') : btnText}
          </button>
        </div>
      </div>
    </div>
  )
}
